import secureStorage from "../../utils/secureStorage";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Shield, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "../../App";

/**
 * Modal untuk minta ibu bapa masukkan emel
 * Untuk fungsi reset password dan notifikasi
 */
export default function EmailLinkModal({ isOpen, onClose, user, onSuccess }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      toast.error("Sila masukkan alamat emel yang sah");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post("/parent/link-email", { email });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success("Emel berjaya dikaitkan! 🎉");
        
        // Update user in localStorage
        const storedUser = JSON.parse(secureStorage.getItem("user") || "{}");
        storedUser.email = email;
        secureStorage.setItem("user", JSON.stringify(storedUser));
        
        // Wait a moment then close
        setTimeout(() => {
          onSuccess?.(email);
          onClose();
        }, 2000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Ralat mengaitkan emel";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Mark as skipped in localStorage (will remind again in 7 days)
    localStorage.setItem("email_link_skipped", Date.now().toString());
    onClose();
  };

  if (success) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md" hideCloseButton>
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mx-auto mb-4 animate-bounce flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-white" style={{ margin: 'auto' }} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Berjaya! 🎉</h3>
            <p className="text-gray-600 mb-4">
              Emel <strong>{email}</strong> telah berjaya disambungkan dengan akaun anda.
            </p>
            <p className="text-sm text-gray-500">
              Sila semak inbox emel anda untuk pengesahan.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" hideCloseButton>
        <DialogHeader>
          <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-center text-xl">
            Tambah Alamat Emel
          </DialogTitle>
          <DialogDescription className="text-center">
            Untuk keselamatan akaun anda
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Info Box */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-gray-800 mb-1">Mengapa perlu emel?</p>
                <ul className="text-gray-600 space-y-1">
                  <li>• Reset kata laluan jika terlupa</li>
                  <li>• Terima notifikasi penting</li>
                  <li>• Backup keselamatan akaun</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Alamat Emel
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contoh@email.com"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleSkip}
                className="flex-1 py-3 rounded-xl"
                disabled={loading}
              >
                Nanti Dulu
              </Button>
              <Button
                type="submit"
                disabled={loading || !email}
                className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Memproses...
                  </span>
                ) : (
                  "Simpan Emel"
                )}
              </Button>
            </div>
          </form>

          <p className="text-xs text-center text-gray-400">
            Kami tidak akan berkongsi emel anda dengan pihak ketiga
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
